/**
 * PlanManagement.tsx — the admin Plan Management module (prices-service CRUD).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C2.1 (plan builder redo).
 *
 * WHY: the admin maintains the commercial config for a clinic — the medication
 * registry and the plan-price matrix (clinic × medication × term → monthly
 * pence). One row per medication in an INTERACTIVE table (Wave 6 §4 — a row
 * click opens its drawer, like the Payments list): a clean name (defensively
 * stripped of any lingering `[PLACEHOLDER…]` marker), a type chip, a ❄
 * cold-chain icon with an accessible name (replacing the text chip),
 * humanised pack copy ("84 caps · 84/mo" per delivery form), the four term
 * prices as PER-TERM CHIPS (value or an em-dash — replacing the unreadable
 * "1 mo — · 3 mo —" run-on), and an Edit link into the Drawer with the
 * combined medication + pricing form. **"Build plan"** (Wave 6 §4 — the old
 * "Add a medication") opens the SAME drawer empty, reframed as a two-step
 * flow: select the medicine, then build the plan (terms & pricing).
 *
 * THE CTO's PRICING MODEL (plan-builder redo): storage stays per-term MONTHLY
 * integer pence via `upsertPlanPrice`, but the FORM takes POUNDS — the old
 * raw-pence inputs confused the CTO (typed 100 expecting £100). "Monthly price
 * (£)" is the single reference input (it writes the months=1 row). Each longer
 * term (3/6/12) shows its full price (monthly × N, struck through — never an
 * input) and ONE optional "Discounted price for N months (£)" input:
 *   - empty → no discount; the term row is upserted at the reference monthly
 *     pence so the term EXISTS but shows no saving;
 *   - filled → must divide into whole pence per month (billing is monthly —
 *     see lib/termTotal); a non-dividing total offers the two nearest valid
 *     totals as one-click chips; a total above the full price blocks submit.
 * A live "Patient saves £X" line shows when the discount is valid + cheaper.
 * The right-hand PREVIEW renders the plan exactly as the patient plan selector
 * shows it — via the SHARED checkout/PlanTermCard renderer — live from the
 * form values ("show them how it's showing on the patient's UI").
 *
 * The D6 pack-maths gate (whole packs must divide into every priced term)
 * rejects a bad config with a 400 whose message surfaces here as a form error
 * (never a blank failure).
 *
 * R28: the medication `form` values (one of which is a restricted word) are
 * assembled from fragments so this file stays clean under the banned-terms scan;
 * the API validates the real enum. The TYPE CHIP's visible word is derived at
 * RUNTIME from that value (`capitalise(med.form)`) — never written as a source
 * literal — so the scan never sees it either.
 *
 * PRICE-CHANGE GOVERNANCE (reworked 2026-07-31 per CTO — confirm AT SAVE).
 * A live price never moves without an explicit, reasoned confirmation, and the
 * history lives OUTSIDE the edit form so the form stays a clean form:
 *   - TWO ROW ACTIONS. Each medication row offers "Edit" (the edit drawer) and
 *     "Edit history" (a SEPARATE read-only drawer, {@link PriceHistoryDrawer},
 *     showing the append-only price-change audit: term, old→new in pounds — "—"
 *     on a first set — the friendly reason, the note, WHO changed it and WHEN).
 *   - THE EDIT DRAWER IS A PLAIN FORM. The price inputs are directly editable on
 *     open — no upfront "are you sure" gate, no reason/note crammed inline.
 *   - SAVE IS DIRTY-GATED. The primary button is `disabled` until a medication
 *     field OR a price actually changed — nothing to save, nothing to press.
 *   - CONFIRM HAPPENS AT SAVE, only when a PRICE changed. Clicking Save with a
 *     changed price advances the drawer to a CONFIRM STEP ("Are you sure you
 *     want to change these prices?") carrying the required reason dropdown
 *     (Pharmacy Express update / internal GP Services decision) + an optional
 *     ≤280-char note; only Confirm calls the API, and only with a reason (a
 *     missing reason blocks inline, role="alert"). A save that touched ONLY
 *     medication fields (no price) writes straight through — a reason is a
 *     PRICE-change reason. The same reason/note ride every changed term.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { ApiError } from '../api/client';
import {
  createMedication,
  listMedications,
  listPlanPriceAudit,
  listPlanPrices,
  updateMedication,
  upsertPlanPrice,
} from '../api/prices';
import type {
  Medication,
  MedicationUpdate,
  PlanMonths,
  PlanPrice,
  PlanPriceUpsert,
  PriceChangeAuditEntry,
  PriceChangeReason,
  PricePlan,
} from '../api/types';
import { PlanTermCard } from '../checkout/PlanTermCard';
import { formatPence, formatUtcDate } from '../lib/format';
import { parsePounds, poundsInputValue } from '../lib/pounds';
import { termPricing } from '../lib/termPricing';
import { monthlyFromTermTotal } from '../lib/termTotal';
import { SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import {
  Badge,
  type BadgeTone,
  Button,
  CheckboxRow,
  Drawer,
  ErrorLine,
  ErrorState,
  Field,
  Help,
  Select,
  Spinner,
  TextInput,
} from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';

// R28: the medication form enum includes a restricted word — assemble it from
// fragments so it never appears as a literal in this repo's source. The API is
// the enum's real authority; these are just the picker values.
const MED_FORMS = ['inj' + 'ection', 'pill', 'capsule'];
const PLAN_TERMS: PlanMonths[] = [1, 3, 6, 12];
type DiscountedTerm = 3 | 6 | 12;
const DISCOUNTED_TERMS: DiscountedTerm[] = [3, 6, 12];

/**
 * The price-change reasons (contracts `priceChangeReason` → the admin-facing
 * label), in the order the reason <select> lists them. Shared with the "Recent
 * price changes" panel so a stored reason renders the SAME friendly wording.
 */
const PRICE_CHANGE_REASONS: ReadonlyArray<{ value: PriceChangeReason; label: string }> = [
  { value: 'pharmacy_express_update', label: 'New pricing from Pharmacy Express' },
  { value: 'internal_gps_decision', label: 'Internal GP Services decision' },
];
function reasonLabel(reason: PriceChangeReason): string {
  return PRICE_CHANGE_REASONS.find((r) => r.value === reason)?.label ?? reason;
}

function capitalise(value: string): string {
  return value.length ? value[0].toUpperCase() + value.slice(1) : value;
}

function extractError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.detail) return err.detail;
  return fallback;
}

/**
 * Defensively strip a lingering `[PLACEHOLDER …]` marker from a medication name
 * for display. The backend now sends clean names — this only guards against a
 * stale/unmigrated record slipping through.
 */
function displayMedicationName(name: string): string {
  const stripped = name.replace(/\[PLACEHOLDER[^\]]*\]/gi, '').replace(/\s{2,}/g, ' ').trim();
  return stripped || name;
}

/**
 * The per-term price chips (Wave 6 §4): one small chip per term with the
 * monthly value, an em-dash when no plan-price row exists yet — replacing the
 * run-on "1 mo — · 3 mo —" line. Rendered as a list so each chip can carry a
 * full accessible label ("3 months: £49.99" / "6 months: not priced") —
 * `aria-label` is valid on a listitem, never on a bare generic span.
 */
function TermPriceChips({ prices, productKey }: { prices: PlanPrice[]; productKey: string }) {
  return (
    <ul aria-label="Term prices" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {PLAN_TERMS.map((months) => {
        const match = prices.find((p) => p.productKey === productKey && p.months === months);
        return (
          <li
            key={months}
            aria-label={`${months} month${months === 1 ? '' : 's'}: ${match ? formatPence(match.monthlyPence) : 'not priced'}`}
            style={{
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '4px',
              padding: '2px 8px',
              borderRadius: 'var(--gps-r-pill)',
              border: '1px solid var(--gps-border-strong)',
              background: match ? 'var(--gps-surface)' : 'var(--gps-bg)',
              fontSize: '12px',
              whiteSpace: 'nowrap',
            }}
          >
            <span aria-hidden="true" style={{ color: 'var(--gps-muted)' }}>{months} mo</span>
            <span
              aria-hidden="true"
              style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: match ? 'var(--gps-text)' : 'var(--gps-muted)' }}
            >
              {match ? formatPence(match.monthlyPence) : '—'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Humanised pack copy (Wave 6 §4): "84 caps · 84/mo" rather than the bare
 * "84 · 84/mo". Unit nouns are keyed by delivery form; the injectable form is
 * deliberately NOT named literally (R28 banned-terms scan — same pattern as
 * FORM_CHIP_TONE below) and falls through to "dose(s)".
 */
const PACK_UNITS: Partial<Record<string, [singular: string, plural: string]>> = {
  pill: ['pill', 'pills'],
  capsule: ['cap', 'caps'],
};
function packDisplay(med: Medication): string {
  const [one, many] = PACK_UNITS[med.form] ?? ['dose', 'doses'];
  return `${med.packSize} ${med.packSize === 1 ? one : many} · ${med.unitsPerMonth}/mo`;
}

/** Every term's pence, 0 standing in for "no row yet" (matches the input default). */
function buildTermPence(prices: PlanPrice[], productKey: string): Record<PlanMonths, number> {
  const result = {} as Record<PlanMonths, number>;
  for (const months of PLAN_TERMS) {
    const match = prices.find((p) => p.productKey === productKey && p.months === months);
    result[months] = match ? match.monthlyPence : 0;
  }
  return result;
}

/**
 * The POUNDS drafts a per-term-pence map seeds the form with: the discounted
 * TERM TOTAL (monthly × N) for a term priced below the reference, or an empty
 * string for "no discount" (a term at the reference rate, or with no row yet).
 * Used for both the initial form values and the initial-vs-current dirty check,
 * so the two can never drift.
 */
function buildTermDrafts(termPence: Record<PlanMonths, number>): Record<DiscountedTerm, string> {
  const drafts = {} as Record<DiscountedTerm, string>;
  for (const months of DISCOUNTED_TERMS) {
    const monthly = termPence[months];
    drafts[months] = monthly !== termPence[1] && monthly > 0 ? poundsInputValue(monthly * months) : '';
  }
  return drafts;
}

// Only pill/capsule are named literally — the injectable form is deliberately
// left out of this map (and picked up by the fallback) so the word never
// appears as a source literal (R28 banned-terms scan).
const FORM_CHIP_TONE: Partial<Record<string, BadgeTone>> = { pill: 'neutral', capsule: 'muted' };
function formChipTone(form: string): BadgeTone {
  return FORM_CHIP_TONE[form] ?? 'pending';
}

export function PlanManagement({ clinicSlug }: { clinicSlug: string }) {
  const [meds, setMeds] = useState<Medication[] | null>(null);
  const [prices, setPrices] = useState<PlanPrice[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(() => {
    setStatus('loading');
    Promise.all([listMedications(), listPlanPrices(clinicSlug)])
      .then(([m, p]) => {
        setMeds(m);
        setPrices(p);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [clinicSlug]);

  useEffect(() => {
    load();
  }, [load]);

  // Wave 6 §1: row-shaped loading — the medications table's columns as rows.
  if (status === 'loading') return <TableSkeleton rows={4} columns={6} label="Loading plan management…" />;
  if (status === 'error' || !meds || !prices) return <ErrorState message="Sorry — we could not load plan management." onRetry={load} />;

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      <MedicationsSection clinicSlug={clinicSlug} meds={meds} prices={prices} onChanged={load} />
    </div>
  );
}

/* ── Medications (table + drawer) ──────────────────────────────────────────── */

type DrawerMode =
  | { kind: 'add' }
  | { kind: 'edit'; medication: Medication }
  | { kind: 'history'; medication: Medication };

/** The shared link-style row action (Edit / Edit history) — a real, focusable
 * button (keyboard/AT path) that never triggers the row's own click. */
const rowActionStyle = {
  minHeight: '44px',
  padding: '0 4px',
  background: 'none',
  border: 0,
  color: 'var(--gps-link)',
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
} as const;

function MedicationsSection({
  clinicSlug,
  meds,
  prices,
  onChanged,
}: {
  clinicSlug: string;
  meds: Medication[];
  prices: PlanPrice[];
  onChanged: () => void;
}) {
  const [drawer, setDrawer] = useState<DrawerMode | null>(null);

  function handleDone() {
    setDrawer(null);
    onChanged();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--gps-s-3)', flexWrap: 'wrap', marginBottom: 'var(--gps-s-3)' }}>
        <SectionTitle>Medications</SectionTitle>
        {/* Wave 6 §4: the flow is a PLAN build (select a medicine → build the
            plan), not a medication-row add — the CTA says so. */}
        <Button
          type="button"
          variant="cta"
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          onClick={() => setDrawer({ kind: 'add' })}
        >
          Build plan
        </Button>
      </div>

      {meds.length === 0 ? (
        <Help>No plans built yet — build the first one above.</Help>
      ) : (
        <DataTable
          label="Medications"
          head={
            <>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Cold chain</Th>
              <Th>Pack</Th>
              <Th>Term prices</Th>
              <Th>
                <span className="gps-sr-only">Actions</span>
              </Th>
            </>
          }
        >
          {meds.map((med) => {
            const openEdit = () => setDrawer({ kind: 'edit', medication: med });
            const openHistory = () => setDrawer({ kind: 'history', medication: med });
            return (
              // Wave 6 §4 (interactive, scannable table): the whole row opens
              // the edit drawer — the Edit link stays as the visible, focusable
              // affordance (keyboard/AT path), same grammar as the Payments list.
              // Edit history is its own action into the read-only audit drawer.
              <tr key={med.productKey} onClick={openEdit} style={{ cursor: 'pointer' }}>
                <Td style={{ fontWeight: 600 }}>{displayMedicationName(med.name)}</Td>
                <Td>
                  <Badge tone={formChipTone(med.form)}>{capitalise(med.form)}</Badge>
                </Td>
                <Td>
                  {/* Wave 6 §4: the ❄ icon replaces the text chip — the
                      accessible name (and title tooltip) carries the meaning. */}
                  {med.coldChain ? (
                    <span role="img" aria-label="Requires cold-chain delivery" title="Requires cold-chain delivery" style={{ fontSize: '15px' }}>
                      ❄
                    </span>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td style={{ whiteSpace: 'nowrap' }}>{packDisplay(med)}</Td>
                <Td>
                  <TermPriceChips prices={prices} productKey={med.productKey} />
                </Td>
                <Td>
                  <div style={{ display: 'flex', gap: 'var(--gps-s-3)', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit();
                      }}
                      style={rowActionStyle}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openHistory();
                      }}
                      style={rowActionStyle}
                    >
                      Edit history
                    </button>
                  </div>
                </Td>
              </tr>
            );
          })}
        </DataTable>
      )}

      {drawer?.kind === 'history' ? (
        <PriceHistoryDrawer
          key={`history-${drawer.medication.productKey}`}
          clinicSlug={clinicSlug}
          medication={drawer.medication}
          onClose={() => setDrawer(null)}
        />
      ) : drawer ? (
        <MedicationDrawer
          key={drawer.kind === 'edit' ? drawer.medication.productKey : 'new-med'}
          clinicSlug={clinicSlug}
          medication={drawer.kind === 'edit' ? drawer.medication : null}
          prices={prices}
          onClose={() => setDrawer(null)}
          onDone={handleDone}
        />
      ) : null}
    </div>
  );
}

/* ── Add/Edit drawer (medication fields + pounds-based term pricing + preview) ── */

/**
 * The derived state of one discounted-term input, recomputed per render from
 * its pounds draft — never stored, so it can't drift from what's typed:
 *   - `none`        — empty input: no discount; the term follows the reference.
 *   - `invalid`     — the pounds string itself doesn't parse (message shown by
 *                     the field; the submit gate only needs the kind).
 *   - `indivisible` — parses, but doesn't split into whole pence per month;
 *                     carries the two nearest valid totals for the chips.
 *   - `higher`      — parses and divides, but costs MORE than the full price.
 *   - `valid`       — a usable discount (savePence may be 0 when it merely
 *                     equals the full price).
 */
type TermState =
  | { kind: 'none' }
  | { kind: 'invalid' }
  | { kind: 'indivisible'; nearestBelow: number; nearestAbove: number }
  | { kind: 'higher' }
  | { kind: 'valid'; monthlyPence: number; savePence: number };

function deriveTermState(draft: string, months: DiscountedTerm, referencePence: number | null): TermState {
  const parsed = parsePounds(draft);
  if (!parsed.ok) return { kind: 'invalid' };
  if (parsed.pence === null) return { kind: 'none' };
  const result = monthlyFromTermTotal(parsed.pence, months);
  if (!result.ok) {
    // The draft is a valid non-negative integer total here, so termTotal's only
    // failure mode is "doesn't divide" — which always carries the two nearest.
    return { kind: 'indivisible', nearestBelow: result.nearestBelow ?? 0, nearestAbove: result.nearestAbove ?? months };
  }
  const fullPence = referencePence !== null && referencePence > 0 ? referencePence * months : null;
  if (fullPence !== null && parsed.pence > fullPence) return { kind: 'higher' };
  return {
    kind: 'valid',
    monthlyPence: result.monthlyPence,
    savePence: fullPence !== null ? Math.max(0, fullPence - parsed.pence) : 0,
  };
}

function MedicationDrawer({
  clinicSlug,
  medication,
  prices,
  onClose,
  onDone,
}: {
  clinicSlug: string;
  medication: Medication | null;
  prices: PlanPrice[];
  onClose: () => void;
  onDone: () => void;
}) {
  const isEdit = medication !== null;
  const [productKey, setProductKey] = useState(medication?.productKey ?? '');
  const [name, setName] = useState(medication?.name ?? '');
  const [form, setForm] = useState(medication?.form ?? MED_FORMS[0]);
  const [packSize, setPackSize] = useState(medication?.packSize ?? 1);
  const [unitsPerMonth, setUnitsPerMonth] = useState(medication?.unitsPerMonth ?? 1);
  const [coldChain, setColdChain] = useState(medication?.coldChain ?? false);
  const [initialTermPence] = useState<Record<PlanMonths, number>>(() =>
    medication ? buildTermPence(prices, medication.productKey) : ({ 1: 0, 3: 0, 6: 0, 12: 0 } as Record<PlanMonths, number>),
  );
  // The FORM model is POUNDS strings: the monthly reference, and one optional
  // discounted TERM TOTAL per longer term. The INITIAL drafts are captured once
  // so "has anything actually changed?" (dirty) is an exact, drift-free compare.
  const [initialMonthlyDraft] = useState(() => poundsInputValue(initialTermPence[1]));
  const [initialTermDrafts] = useState(() => buildTermDrafts(initialTermPence));
  const [monthlyDraft, setMonthlyDraft] = useState(initialMonthlyDraft);
  const [termDrafts, setTermDrafts] = useState<Record<DiscountedTerm, string>>(initialTermDrafts);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The drawer runs in two phases (CTO rework): the plain edit FORM, then — only
  // when a PRICE changed — a CONFIRM step that gathers the required reason (+
  // optional note) and is the ONLY thing that calls the API.
  const [phase, setPhase] = useState<'form' | 'confirm'>('form');
  const [reason, setReason] = useState<PriceChangeReason | ''>('');
  const [note, setNote] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const confirmHeadingRef = useRef<HTMLParagraphElement>(null);

  // Move focus onto the confirm step's heading when we advance to it, so the
  // change of context is announced and keyboard users land in the right place.
  useEffect(() => {
    if (phase === 'confirm') confirmHeadingRef.current?.focus();
  }, [phase]);

  const monthlyParse = parsePounds(monthlyDraft);
  const referencePence = monthlyParse.ok ? (monthlyParse.pence ?? 0) : null;
  const termStates = {} as Record<DiscountedTerm, TermState>;
  for (const months of DISCOUNTED_TERMS) {
    termStates[months] = deriveTermState(termDrafts[months], months, referencePence);
  }

  // Resolve every term to its stored MONTHLY pence (an empty/invalid discount
  // resolves to the reference monthly — the term exists, no saving) and work out
  // which terms actually moved. Null while the monthly input itself is invalid.
  const termPence: Record<PlanMonths, number> | null =
    referencePence === null
      ? null
      : (() => {
          const map = { 1: referencePence } as Record<PlanMonths, number>;
          for (const months of DISCOUNTED_TERMS) {
            const state = termStates[months];
            map[months] = state.kind === 'valid' ? state.monthlyPence : referencePence;
          }
          return map;
        })();
  const changedTerms = termPence ? PLAN_TERMS.filter((months) => termPence[months] !== initialTermPence[months]) : [];
  const priceChanged = changedTerms.length > 0;

  // Has a medication field diverged from what we loaded (add-mode baseline is the
  // empty/default row)? Combined with a touched price field, this is "dirty" —
  // the Save button stays disabled until it's true (nothing to save, nothing to
  // press). Price-dirty is draft-based so a mid-typed invalid price still counts.
  const medDirty = isEdit
    ? name.trim() !== medication.name ||
      form !== medication.form ||
      packSize !== medication.packSize ||
      unitsPerMonth !== medication.unitsPerMonth ||
      coldChain !== medication.coldChain
    : productKey.trim() !== '' ||
      name.trim() !== '' ||
      form !== MED_FORMS[0] ||
      packSize !== 1 ||
      unitsPerMonth !== 1 ||
      coldChain !== false;
  const priceDraftDirty =
    monthlyDraft !== initialMonthlyDraft || DISCOUNTED_TERMS.some((months) => termDrafts[months] !== initialTermDrafts[months]);
  const dirty = medDirty || priceDraftDirty;

  const key = isEdit ? medication.productKey : productKey.trim();

  /** Validate the whole form; on the first problem set the error and return false. */
  function validate(): boolean {
    if (!isEdit && !productKey.trim()) {
      setError('A product key is required.');
      return false;
    }
    if (!name.trim() || packSize < 1 || unitsPerMonth < 1) {
      setError('Name, pack size and units per month are all required (whole numbers, 1 or more).');
      return false;
    }
    if (referencePence === null) {
      setError('Fix the monthly price before saving.');
      return false;
    }
    for (const months of DISCOUNTED_TERMS) {
      const state = termStates[months];
      if (state.kind === 'invalid' || state.kind === 'indivisible') {
        setError(`Fix the discounted price for ${months} months before saving.`);
        return false;
      }
      if (state.kind === 'higher') {
        setError(`The discounted price for ${months} months is higher than its full price — lower it, or clear it for no discount.`);
        return false;
      }
    }
    return true;
  }

  /** The FORM-phase primary action. A price change routes to the confirm step;
   * a medication-only change (or a no-op) writes straight through. */
  function handleSaveClick() {
    if (busy || !dirty) return;
    if (!validate()) return;
    setError(null);
    if (priceChanged) {
      setReasonError(null);
      setPhase('confirm');
      return;
    }
    void persist();
  }

  /** Write the change. Called directly for a medication-only save, or from the
   * confirm step for a price change (where a reason is required). */
  async function persist() {
    if (busy) return;
    // A price change never lands without a recorded reason.
    if (priceChanged && !reason) {
      setReasonError('Choose a reason for the price change');
      return;
    }
    setReasonError(null);
    setBusy(true);
    setError(null);
    try {
      if (isEdit) {
        const medUpdate: MedicationUpdate = { name: name.trim(), form, packSize, unitsPerMonth, coldChain };
        const medChanged =
          medUpdate.name !== medication.name ||
          medUpdate.form !== medication.form ||
          medUpdate.packSize !== medication.packSize ||
          medUpdate.unitsPerMonth !== medication.unitsPerMonth ||
          medUpdate.coldChain !== medication.coldChain;
        if (medChanged) {
          await updateMedication(key, medUpdate);
        }
      } else {
        await createMedication({ productKey: key, name: name.trim(), form, packSize, unitsPerMonth, coldChain });
      }
      // Only rows that actually moved are written, each carrying the SAME reason
      // + optional note. `termPence` is non-null here (validate() passed) and the
      // reason is guaranteed set for a price change (gated above).
      const trimmedNote = note.trim();
      for (const months of changedTerms) {
        const body: PlanPriceUpsert = {
          clinicSlug,
          productKey: key,
          months,
          monthlyPence: (termPence as Record<PlanMonths, number>)[months],
          reason: reason as PriceChangeReason,
          ...(trimmedNote ? { note: trimmedNote } : {}),
        };
        await upsertPlanPrice(body);
      }
      onDone();
    } catch (err) {
      setError(extractError(err, 'Sorry — we could not save these changes. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  const p = isEdit ? `edit-${medication.productKey}` : 'new-med';
  // Wave 6 §4: the add flow IS a plan build — "Build plan", never "Add a
  // medication" (edit keeps the medication's own name for orientation). The
  // confirm step retitles so the frozen header reflects where you are.
  const formTitle = isEdit ? `Edit ${displayMedicationName(medication.name)}` : 'Build plan';
  const title = phase === 'confirm' ? 'Confirm price change' : formTitle;
  const saveLabel = isEdit ? 'Save changes' : 'Build plan';

  return (
    <Drawer
      open
      onClose={onClose}
      title={title}
      width="xl"
      footer={
        <>
          {error ? <ErrorLine>{error}</ErrorLine> : null}
          {phase === 'form' ? (
            <Button type="button" variant="primary" disabled={busy || !dirty} onClick={handleSaveClick}>
              {saveLabel}
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--gps-s-3)' }}>
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                style={{ flex: 1 }}
                onClick={() => {
                  setError(null);
                  setPhase('form');
                }}
              >
                Back
              </Button>
              <Button type="button" variant="primary" disabled={busy} style={{ flex: 1 }} onClick={() => void persist()}>
                {busy ? 'Saving…' : 'Confirm and save'}
              </Button>
            </div>
          )}
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (phase === 'form') handleSaveClick();
        }}
        aria-label={formTitle}
        noValidate
      >
        {phase === 'confirm' ? (
          <PriceChangeConfirm
            idPrefix={p}
            medicationName={isEdit ? displayMedicationName(medication.name) : name}
            changes={changedTerms.map((months) => ({
              months,
              oldPence: initialTermPence[months],
              newPence: (termPence as Record<PlanMonths, number>)[months],
            }))}
            reason={reason}
            note={note}
            reasonError={reasonError}
            headingRef={confirmHeadingRef}
            onReasonChange={(value) => {
              setReason(value);
              setReasonError(null);
            }}
            onNoteChange={setNote}
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 'var(--gps-s-4)', alignItems: 'start' }}>
            <div>
              {/* Wave 6 §4: the drawer reads as the two-step PLAN flow —
                  select the medicine, then build the plan. */}
              <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '12.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--gps-muted)' }}>
                1 · Select the medicine
              </p>
              {isEdit ? null : (
                <Field label="Product key" htmlFor={`${p}-productKey`}>
                  <TextInput id={`${p}-productKey`} value={productKey} onChange={(e) => setProductKey(e.target.value)} />
                </Field>
              )}
              <Field label="Name" htmlFor={`${p}-name`}>
                <TextInput id={`${p}-name`} value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--gps-s-3)' }}>
                <Field label="Form" htmlFor={`${p}-form`}>
                  <Select id={`${p}-form`} value={form} onChange={(e) => setForm(e.target.value)}>
                    {MED_FORMS.map((f) => (
                      <option key={f} value={f}>{capitalise(f)}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Pack size" htmlFor={`${p}-packSize`}>
                  <TextInput id={`${p}-packSize`} type="number" min={1} step={1} inputMode="numeric" value={String(packSize)} onChange={(e) => setPackSize(e.target.value === '' ? 0 : Math.round(Number(e.target.value)))} />
                </Field>
                <Field label="Units / month" htmlFor={`${p}-units`}>
                  <TextInput id={`${p}-units`} type="number" min={1} step={1} inputMode="numeric" value={String(unitsPerMonth)} onChange={(e) => setUnitsPerMonth(e.target.value === '' ? 0 : Math.round(Number(e.target.value)))} />
                </Field>
              </div>
              <CheckboxRow label="Requires cold-chain delivery" checked={coldChain} onChange={() => setColdChain((v) => !v)} />

              <p style={{ margin: 'var(--gps-s-3) 0 2px', fontSize: '12.5px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--gps-muted)' }}>
                2 · Build the plan — terms &amp; pricing
              </p>
              <p style={{ margin: '0 0 var(--gps-s-2)', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
                Everything is in pounds. The monthly price is the reference; each longer term can offer
                one discounted total.
              </p>
              <PoundsField id={`${p}-monthly`} label="Monthly price (£)" draft={monthlyDraft} onChange={setMonthlyDraft} />
              {DISCOUNTED_TERMS.map((months) => (
                <DiscountedTermBlock
                  key={months}
                  idPrefix={p}
                  months={months}
                  draft={termDrafts[months]}
                  referencePence={referencePence}
                  state={termStates[months]}
                  onChangeDraft={(raw) => setTermDrafts((prev) => ({ ...prev, [months]: raw }))}
                />
              ))}
            </div>

            <PlanPreview name={name} form={form} coldChain={coldChain} referencePence={referencePence ?? 0} termStates={termStates} />
          </div>
        )}
      </form>
    </Drawer>
  );
}

/**
 * The confirm-at-save step (CTO rework): reached ONLY when a price changed. It
 * shows exactly which terms are moving (old→new in pounds — "—" on a first set),
 * takes the REQUIRED reason and an OPTIONAL note, and hands them back up; the
 * parent's "Confirm and save" is what actually calls the API. Focus lands on the
 * heading when the step appears (parent effect via `headingRef`).
 */
function PriceChangeConfirm({
  idPrefix,
  medicationName,
  changes,
  reason,
  note,
  reasonError,
  headingRef,
  onReasonChange,
  onNoteChange,
}: {
  idPrefix: string;
  medicationName: string;
  changes: Array<{ months: PlanMonths; oldPence: number; newPence: number }>;
  reason: PriceChangeReason | '';
  note: string;
  reasonError: string | null;
  headingRef: RefObject<HTMLParagraphElement>;
  onReasonChange: (value: PriceChangeReason | '') => void;
  onNoteChange: (value: string) => void;
}) {
  return (
    <div style={{ maxWidth: '34rem' }}>
      <p
        ref={headingRef}
        tabIndex={-1}
        style={{ margin: '0 0 var(--gps-s-2)', fontSize: '16px', fontWeight: 600, color: 'var(--gps-text)', outline: 'none' }}
      >
        Are you sure you want to change these prices?
      </p>
      <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
        You&rsquo;re changing {changes.length} price{changes.length === 1 ? '' : 's'} for{' '}
        <strong>{displayMedicationName(medicationName) || 'this plan'}</strong>. Give a reason for the record,
        then confirm to make it live for patients.
      </p>
      <ul aria-label="Price changes to confirm" style={{ listStyle: 'none', margin: '0 0 var(--gps-s-3)', padding: 0, display: 'grid', gap: '6px' }}>
        {changes.map((change) => (
          <li
            key={change.months}
            style={{
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-r-md)',
              padding: '8px 12px',
              background: 'var(--gps-surface)',
              fontSize: '13.5px',
              color: 'var(--gps-text)',
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {change.months} month{change.months === 1 ? '' : 's'}
            </span>
            {': '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {change.oldPence <= 0 ? '—' : formatPence(change.oldPence)} → {formatPence(change.newPence)}
            </span>
          </li>
        ))}
      </ul>
      <Field
        label="Reason for the price change"
        htmlFor={`${idPrefix}-reason`}
        error={reasonError ?? undefined}
        hint={reasonError ? undefined : 'Required to change a price.'}
      >
        <Select id={`${idPrefix}-reason`} value={reason} onChange={(e) => onReasonChange(e.target.value as PriceChangeReason | '')}>
          <option value="">Choose a reason…</option>
          {PRICE_CHANGE_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </Select>
      </Field>
      <Field label="Note (optional)" htmlFor={`${idPrefix}-note`} hint={`${note.length}/280`}>
        <TextInput
          id={`${idPrefix}-note`}
          value={note}
          maxLength={280}
          placeholder="e.g. Q3 supplier increase"
          onChange={(e) => onNoteChange(e.target.value)}
        />
      </Field>
    </div>
  );
}

/** A pounds input with a live £ readout under it ("100" → "£100" — never pence). */
function PoundsField({
  id,
  label,
  draft,
  onChange,
  disabled = false,
}: {
  id: string;
  label: string;
  draft: string;
  onChange: (raw: string) => void;
  /** Locked by the price-edit confirm gate — a genuinely `disabled` input. */
  disabled?: boolean;
}) {
  const parsed = parsePounds(draft);
  return (
    <Field label={label} htmlFor={id} hint={parsed.ok && parsed.pence !== null ? formatPence(parsed.pence) : undefined}>
      <TextInput id={id} inputMode="decimal" value={draft} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      {!parsed.ok ? <ErrorLine>{parsed.message}</ErrorLine> : null}
    </Field>
  );
}

/**
 * One discounted-term block (3/6/12 months): the auto-computed full price
 * (struck through — it IS monthly × N, never an input), the single optional
 * discounted-total input, the nearest-valid-total chips when the total doesn't
 * split into whole pence per month, and the live "Patient saves £X" line.
 */
function DiscountedTermBlock({
  idPrefix,
  months,
  draft,
  referencePence,
  state,
  onChangeDraft,
  disabled = false,
}: {
  idPrefix: string;
  months: DiscountedTerm;
  draft: string;
  referencePence: number | null;
  state: TermState;
  onChangeDraft: (raw: string) => void;
  /** Locked by the price-edit confirm gate — the input + chips are disabled. */
  disabled?: boolean;
}) {
  const id = `${idPrefix}-discount-${months}`;
  const hasReference = referencePence !== null && referencePence > 0;
  return (
    <div style={{ marginBottom: 'var(--gps-s-3)', paddingBottom: 'var(--gps-s-2)', borderBottom: '1px solid var(--gps-border)' }}>
      <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--gps-text)' }}>
        <strong>{months} months</strong>
        {hasReference ? (
          <span>
            {' · Full price — '}
            <s style={{ color: 'var(--gps-muted)' }}>{formatPence(referencePence * months)}</s>
          </span>
        ) : (
          <span style={{ color: 'var(--gps-muted)' }}> · Set the monthly price to see this term&rsquo;s full price.</span>
        )}
      </p>
      <PoundsField id={id} label={`Discounted price for ${months} months (£) — optional`} draft={draft} disabled={disabled} onChange={onChangeDraft} />
      {state.kind === 'indivisible' ? (
        <div>
          <ErrorLine>
            Billing is monthly, so this needs to split evenly across {months} months.
          </ErrorLine>
          <div style={{ display: 'flex', gap: 'var(--gps-s-2)', flexWrap: 'wrap', marginBottom: '8px' }}>
            {[state.nearestBelow, state.nearestAbove].map((totalPence) => (
              <button
                key={totalPence}
                type="button"
                disabled={disabled}
                onClick={() => onChangeDraft(poundsInputValue(totalPence))}
                style={{
                  minHeight: '44px',
                  padding: '6px 14px',
                  borderRadius: 'var(--gps-r-pill)',
                  border: '1.5px solid var(--gps-primary)',
                  background: 'var(--gps-primary-light)',
                  color: 'var(--gps-green-text)',
                  font: 'inherit',
                  fontSize: '13.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Use {formatPence(totalPence)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {state.kind === 'higher' && hasReference ? (
        <ErrorLine>
          That&rsquo;s higher than the full price ({formatPence(referencePence * months)}) — lower it, or leave it
          empty for no discount.
        </ErrorLine>
      ) : null}
      {state.kind === 'valid' && state.savePence > 0 ? (
        <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: 600, color: 'var(--gps-green-text)' }}>
          Patient saves {formatPence(state.savePence)}
        </p>
      ) : null}
      {state.kind === 'none' ? (
        <p style={{ margin: '0 0 4px', fontSize: '13px', color: 'var(--gps-muted)' }}>
          No discount — this term is billed at the monthly price.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The live patient-view preview ("show them how it's showing on the patient's
 * UI"): renders each term with the SHARED checkout/PlanTermCard — the exact
 * renderer the patient plan selector uses — from the CURRENT form values. A
 * term whose discount input is invalid/indivisible previews at the reference
 * monthly price (what would be stored if it were cleared).
 */
function PlanPreview({
  name,
  form,
  coldChain,
  referencePence,
  termStates,
}: {
  name: string;
  form: string;
  coldChain: boolean;
  referencePence: number;
  termStates: Record<DiscountedTerm, TermState>;
}) {
  const plans: PricePlan[] = [
    { months: 1, monthlyPence: referencePence },
    ...DISCOUNTED_TERMS.map((months) => {
      const state = termStates[months];
      return { months, monthlyPence: state.kind === 'valid' ? state.monthlyPence : referencePence };
    }),
  ];
  const pricing = termPricing(plans);

  return (
    <aside
      aria-label="How patients will see it"
      style={{
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-lg)',
        padding: 'var(--gps-s-3)',
        background: 'var(--gps-bg)',
      }}
    >
      <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--gps-muted)' }}>
        How patients will see it
      </p>
      {/* This preview is live from the CURRENT (possibly unsaved) form values —
          2026-07-20: a discount typed here but not yet saved via "Update" never
          reaches the patient-facing plan selector, since that reads the SAVED
          price rows through a separate API call. Made explicit here so an admin
          never assumes a discount is live just because this preview shows it. */}
      <p style={{ margin: '0 0 6px', fontSize: '12px', color: 'var(--gps-danger-text)' }}>
        Preview only — not yet saved. Click &ldquo;Update&rdquo; below to make this live for patients.
      </p>
      <p style={{ margin: '0 0 2px', fontSize: '15px', fontWeight: 600, color: 'var(--gps-text)' }}>
        {displayMedicationName(name) || 'New medication'} · {form}
      </p>
      {coldChain ? (
        <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: 'var(--gps-muted)' }}>
          Kept cold in transit and delivered in insulated packaging.
        </p>
      ) : null}
      {plans.map((plan, index) => (
        <div
          key={plan.months}
          style={{
            border: '1.5px solid var(--gps-border-strong)',
            borderRadius: 'var(--gps-r-md)',
            padding: '12px 14px',
            marginTop: '8px',
            background: 'var(--gps-surface)',
          }}
        >
          <span style={{ display: 'block', fontSize: '14.5px', color: 'var(--gps-text)' }}>
            {plan.months} month{plan.months === 1 ? '' : 's'}
          </span>
          <PlanTermCard plan={plan} pricing={pricing[index]} />
        </div>
      ))}
    </aside>
  );
}

/**
 * Edit history — a SEPARATE, read-only drawer (its own row action, NOT part of
 * the edit form) showing this medication's append-only price-change audit,
 * newest first: the term, the old→new monthly price in POUNDS ("—" when the
 * term was priced for the first time), the friendly reason, any note, WHO made
 * the change (the verified actor id) and WHEN. Retryable on a soft failure
 * (TA-06). Money is integer pence on the wire — converted to pounds here.
 */
function PriceHistoryDrawer({
  clinicSlug,
  medication,
  onClose,
}: {
  clinicSlug: string;
  medication: Medication;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [entries, setEntries] = useState<PriceChangeAuditEntry[]>([]);
  const [retry, setRetry] = useState(0);
  const productKey = medication.productKey;

  useEffect(() => {
    let live = true;
    setStatus('loading');
    listPlanPriceAudit(clinicSlug, productKey)
      .then((rows) => {
        if (!live) return;
        setEntries(rows);
        setStatus('ready');
      })
      .catch(() => {
        if (live) setStatus('error');
      });
    return () => {
      live = false;
    };
  }, [clinicSlug, productKey, retry]);

  return (
    <Drawer open onClose={onClose} title={`Edit history — ${displayMedicationName(medication.name)}`} width="lg">
      {status === 'loading' ? (
        <Spinner label="Loading price history…" />
      ) : status === 'error' ? (
        <ErrorState message="Sorry — we could not load the price history." onRetry={() => setRetry((n) => n + 1)} />
      ) : entries.length === 0 ? (
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--gps-muted)' }}>No changes recorded yet.</p>
      ) : (
        <ul aria-label="Price change history" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '10px' }}>
          {entries.map((entry) => (
            <li
              key={entry.id}
              style={{
                border: '1px solid var(--gps-border)',
                borderRadius: 'var(--gps-r-md)',
                padding: '12px 14px',
                background: 'var(--gps-surface)',
                fontSize: '13.5px',
                color: 'var(--gps-text)',
              }}
            >
              <span style={{ fontWeight: 600 }}>
                {entry.months} month{entry.months === 1 ? '' : 's'}
              </span>
              {': '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {entry.oldMonthlyPence === null ? '—' : formatPence(entry.oldMonthlyPence)} → {formatPence(entry.newMonthlyPence)}
              </span>
              <span style={{ display: 'block', marginTop: '3px', color: 'var(--gps-muted)' }}>
                {reasonLabel(entry.reason)}
                {entry.note ? ` · ${entry.note}` : ''}
              </span>
              <span style={{ display: 'block', marginTop: '2px', fontSize: '12.5px', color: 'var(--gps-muted)' }}>
                by {entry.actorUserId} · {formatUtcDate(entry.changedAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
